import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "8px",
        border: active ? "2px solid #2563eb" : "2px solid transparent",
        background: active ? "#dbeafe" : "transparent",
        color: active ? "#1e3a5f" : "#4b6b8a",
        fontWeight: "bold",
        fontSize: "14px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder, rows = 4, onSubmit }) {
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  const editor = useEditor({
extensions: [StarterKit],
    content: value || "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      const isEmpty = editor.isEmpty;
      onChange(isEmpty ? "" : html);
    },
    editorProps: {
      attributes: {
        style: [
          "min-height:" + (rows * 24) + "px",
          "outline:none",
          "padding:10px 14px",
          "color:#1e3a5f",
          "font-size:14px",
          "line-height:1.6",
          "font-family:inherit",
        ].join(";"),
      },
      handleKeyDown(_view, event) {
        if (onSubmitRef.current && event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          onSubmitRef.current();
          return true;
        }
        return false;
      },
    },
  });

  // Sync external value changes (e.g. when edit form opens with existing content)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const isBold = editor.isActive("bold");
  const isItalic = editor.isActive("italic");
  const isUnderline = editor.isActive("underline");

  return (
    <div style={{
      border: "2px solid #bfdbfe",
      borderRadius: "14px",
      background: "#f5faff",
      overflow: "hidden",
    }}>
      {/* Toolbar */}
      <div style={{
        display: "flex",
        gap: "2px",
        padding: "6px 8px",
        borderBottom: "2px solid #bfdbfe",
        background: "#f0f7ff",
      }}>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={isBold} title="Bold">
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={isItalic} title="Italic">
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={isUnderline} title="Underline">
          <span style={{ textDecoration: "underline" }}>U</span>
        </ToolbarBtn>
        <div style={{ width: "1px", background: "#bfdbfe", margin: "2px 4px" }} />
        <ToolbarBtn onClick={() => editor.chain().focus().setParagraph().run()} active={false} title="Paragraph">
          ¶
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
          ≡
        </ToolbarBtn>
      </div>

      {/* Editor area */}
      <div style={{ position: "relative" }}>
        {editor.isEmpty && placeholder && (
          <div style={{
            position: "absolute",
            top: "10px",
            left: "14px",
            color: "#9ca3af",
            fontSize: "14px",
            pointerEvents: "none",
            userSelect: "none",
          }}>
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/**
 * Renders saved HTML content safely.
 * Falls back to pre-wrap plain text for legacy plain-text entries.
 */
export function RichTextDisplay({ html, style = {} }) {
  if (!html) return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(html);
  if (isHtml) {
    return (
      <div
        className="rich-content"
        dangerouslySetInnerHTML={{ __html: html }}
        style={style}
      />
    );
  }
  return <div style={{ whiteSpace: "pre-wrap", ...style }}>{html}</div>;
}
